import { Camera } from 'lucide-react';
import type { CameraErrorReason, CameraViewProps, QRTranslation } from './types';

/**
 * 실패 사유별 사용자 안내 메시지 선택 (#662)
 * - no-device/unknown/null 은 기존 일반 메시지 유지
 */
function getCameraErrorMessage(reason: CameraErrorReason | null, t: QRTranslation): string {
  switch (reason) {
    case 'permission-denied':
      return t.cameraPermissionDenied;
    case 'unsupported':
      return t.cameraUnsupported;
    case 'in-use':
      return t.cameraInUse;
    default:
      return t.cameraError;
  }
}

export function CameraView({ cameraStatus, cameraError, videoRef, onCameraRetry, translation: t }: CameraViewProps) {
  if (cameraStatus === 'active') {
    return (
      <div className="w-full h-full relative">
        <video
          ref={videoRef}
          className="w-full h-full object-cover rounded-lg"
          autoPlay
          playsInline
          muted
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/10 rounded-lg opacity-0 hover:opacity-100 transition-opacity">
          <p className="text-white bg-black/50 px-3 py-1 rounded-full text-sm">
            {t.clickToScan}
          </p>
        </div>
      </div>
    );
  }

  if (cameraStatus === 'starting') {
    return (
      <div className="w-full h-full bg-muted rounded-lg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary/20 border-t-primary mx-auto mb-2"></div>
          <p className="text-foreground text-sm">{t.cameraStarting}</p>
        </div>
      </div>
    );
  }

  if (cameraStatus === 'error') {
    return (
      <button
        type="button"
        onClick={onCameraRetry}
        aria-label={t.cameraRetry}
        className="w-full h-full bg-muted rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors border-0 p-0"
      >
        <div className="text-center px-2">
          <Camera className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground text-sm">{getCameraErrorMessage(cameraError, t)}</p>
          {cameraError === 'permission-denied' && (
            <p className="text-muted-foreground text-xs mt-1">{t.cameraPermissionHint}</p>
          )}
          <p className="text-primary text-xs mt-1">{t.cameraRetry}</p>
        </div>
      </button>
    );
  }

  // Default placeholder (idle)
  return (
    <button
      type="button"
      onClick={onCameraRetry}
      aria-label={t.clickToScan}
      className="w-full h-full bg-muted rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors border-0 p-0"
    >
      <div className="text-center">
        <Camera className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
        <p className="text-primary text-xs">{t.clickToScan}</p>
      </div>
    </button>
  );
}
